import { Link } from "react-router-dom";
import {
    Users, Workflow, FileSearch, Bot, ArrowUpRight, Check, ShieldCheck,
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import PageHero from "../components/sections/PageHero";
import FinalCTA from "../components/sections/FinalCTA";
import { PRODUCTS } from "../data/products";

const ICONS = {
    talentiqx: <Users className="w-6 h-6" />,
    recruitiqx: <Workflow className="w-6 h-6" />,
    docuiqx: <FileSearch className="w-6 h-6" />,
    agentiqx: <Bot className="w-6 h-6" />,
};

export default function Products() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="Products & Platforms"
                description="Perceptiqx products & platforms — TalentIQX, RecruitIQX, DocuIQX and AgentIQX — productised AI accelerators for talent, recruitment, documents and enterprise agents."
            />

            <PageHero
                eyebrow="Products & platforms"
                title="AI accelerators your team"
                titleAccent="can adopt in days."
                description="Talent intelligence, recruitment operations, document intelligence, and enterprise agents — configurable around your workflows."
                primaryCta={{ to: "/contact", label: "Book an accelerator walkthrough" }}
                secondaryCta={{ to: "/ai-solutions", label: "Custom AI services" }}
                bullets={["TalentIQX", "RecruitIQX", "DocuIQX", "AgentIQX"]}
            />

            <section className="pqx-section bg-[#FAFAF8]" data-testid="products-list">
                <div className="pqx-container space-y-6">
                    {PRODUCTS.map((p, i) => (
                        <article
                            key={p.slug}
                            data-testid={`product-card-${p.slug}`}
                            className="group relative overflow-hidden rounded-3xl bg-white border border-[#303030]/8 p-7 md:p-12 hover:border-[#FF4040]/30 hover:shadow-[0_30px_70px_-30px_rgba(255,64,64,0.25)] transition-all"
                        >
                            <div
                                className="absolute inset-y-0 -right-32 w-[420px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{
                                    background: "radial-gradient(closest-side, rgba(255,64,64,0.12), transparent 70%)",
                                }}
                            />

                            <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-10">
                                <div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <span className="w-12 h-12 grid place-items-center rounded-2xl bg-[#303030] text-[#FAFAF8] group-hover:bg-[#FF4040] transition-colors">
                                                {ICONS[p.slug]}
                                            </span>
                                            <span className="font-mono text-[11px] text-[#303030]/55 uppercase tracking-[0.18em]">
                                                Accelerator 0{i + 1}
                                            </span>
                                        </div>
                                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#FF4040]/10 text-[#FF4040] border border-[#FF4040]/20">
                                            {p.maturity}
                                        </span>
                                    </div>
                                    <h2 className="pqx-display text-3xl md:text-4xl text-[#303030] mt-6">
                                        {p.name}
                                    </h2>
                                    <p className="mt-2 text-[13px] uppercase tracking-[0.18em] text-[#FF4040] font-semibold">
                                        {p.tagline}
                                    </p>

                                    <div className="mt-6 space-y-4">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#303030]/45 font-semibold">
                                                Who it is for
                                            </p>
                                            <p className="mt-1 text-[14px] text-[#303030]/80 font-medium">
                                                {p.whoFor}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#303030]/45 font-semibold">
                                                First use case
                                            </p>
                                            <p className="mt-1 text-[14px] text-[#303030]/80 font-medium">
                                                {p.firstUseCase}
                                            </p>
                                        </div>
                                    </div>

                                    <Link
                                        to={`/products/${p.slug}`}
                                        data-testid={`product-cta-${p.slug}`}
                                        className="mt-8 inline-flex pqx-btn-primary"
                                    >
                                        Explore product
                                        <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </div>

                                <div className="rounded-2xl bg-[#FAFAF8] border border-[#303030]/8 p-7 flex flex-col justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.2em] text-[#303030]/45">
                                            Key capabilities
                                        </p>
                                        <ul className="mt-5 space-y-3">
                                            {p.capabilities.slice(0, 3).map((c) => (
                                                <li
                                                    key={c}
                                                    className="flex items-start gap-3 text-[14px] text-[#303030]/85"
                                                >
                                                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#FF4040]/12 text-[#FF4040] grid place-items-center shrink-0">
                                                        <Check className="w-3 h-3" />
                                                    </span>
                                                    {c}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* Consolidated Enterprise Security Strip */}
            <section className="bg-white border-t border-b border-[#303030]/8 py-10">
                <div className="pqx-container">
                    <div className="max-w-4xl mx-auto text-center lg:text-left grid lg:grid-cols-[1fr_2fr] gap-6 items-center">
                        <div>
                            <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#FF4040]/10 text-[#FF4040] mx-auto lg:mx-0">
                                <ShieldCheck className="w-5 h-5" />
                            </span>
                            <h3 className="pqx-display text-xl text-[#303030] mt-3">
                                Enterprise-ready by design.
                            </h3>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4 text-left">
                            {[
                                "Role-based access controls (RBAC)",
                                "Audit trails for compliance",
                                "Secure data handling (encryption)",
                                "Configurable workflow logic",
                            ].map((bullet) => (
                                <div key={bullet} className="flex items-center gap-3">
                                    <span className="w-5 h-5 rounded-full bg-[#FF4040]/10 text-[#FF4040] flex items-center justify-center shrink-0">
                                        <Check className="w-3 h-3" />
                                    </span>
                                    <span className="text-[13.5px] font-medium text-[#303030]/80">{bullet}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <FinalCTA
                title="See an accelerator with your data."
                description="Book a guided walkthrough — we'll demo the accelerator most relevant to your team with sample data shaped like yours, and a 30-day rollout plan."
                primary={{ to: "/contact", label: "Book an accelerator walkthrough" }}
                secondary={{ to: "/ai-solutions", label: "Custom AI services" }}
            />
        </div>
    );
}
